import { useState } from "react";
import { Download, Trash2, RefreshCw, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { http, errorMessage } from "../../lib/api";

export const STATUS_LABEL = { neu: "Neu", in_bearbeitung: "In Bearbeitung", kontaktiert: "Kontaktiert", abgeschlossen: "Abgeschlossen", abgelehnt: "Abgelehnt" };

const LABELS = {
  inquiries: [["company", "Unternehmen"], ["contact_name", "Ansprechpartner"], ["email", "E-Mail"], ["phone", "Telefon"], ["industry", "Branche"], ["position", "Gesuchter Beruf"], ["headcount", "Anzahl"], ["location", "Einsatzort"], ["start_date", "Gewünschter Beginn"], ["message", "Nachricht"], ["marketing_consent", "Einwilligung Informationen"]],
  applicants: [["first_name", "Vorname"], ["last_name", "Nachname"], ["email", "E-Mail"], ["phone", "Telefon"], ["country", "Wohnsitzland"], ["profession", "Beruf"], ["experience", "Berufserfahrung"], ["german_level", "Deutschkenntnisse"], ["region", "Arbeitsregion"], ["availability", "Früheste Verfügbarkeit"], ["message", "Nachricht"], ["talent_pool_consent", "Einwilligung Talentpool"], ["news_consent", "Einwilligung Stellen-Infos"], ["language", "Formularsprache"]],
};

const show = (v) => (typeof v === "boolean" ? (v ? "Ja" : "Nein") : v || "–");

export const DetailSheet = ({ kind, item, onClose, onChanged, odooReady }) => {
  const [note, setNote] = useState(item?.internal_note ?? "");
  const [busy, setBusy] = useState("");

  const patch = async (body) => {
    try { const { data } = await http.patch(`/admin/${kind}/${item.id}`, body); onChanged(data); toast.success("Gespeichert"); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const download = async (f) => {
    setBusy(f.file_id);
    try {
      const res = await http.get(`/admin/applicants/${item.id}/files/${f.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = f.filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("Datei konnte nicht geladen werden."); }
    finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm(`Eintrag ${item.reference} endgültig löschen? Dies kann nicht rückgängig gemacht werden.`)) return;
    try { await http.delete(`/admin/${kind}/${item.id}`); toast.success("Eintrag gelöscht"); onChanged(null, item.id); onClose(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const sync = async () => {
    setBusy("odoo");
    try { const { data } = await http.post(`/admin/${kind}/${item.id}/odoo-sync`); onChanged({ ...item, odoo: data }); toast.message(`Odoo: ${data?.status}`); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(""); }
  };

  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="admin-detail-sheet">
        {item && (
          <>
            <SheetHeader>
              <SheetTitle className="text-[color:var(--navy)]">{item.reference}</SheetTitle>
              <SheetDescription>{item.kind_label} · eingegangen {item.created_at_display} · Herkunft: {item.source}</SheetDescription>
            </SheetHeader>
            <dl className="mt-6 divide-y divide-slate-100 text-sm">
              {LABELS[kind].map(([k, l]) => (
                <div key={k} className="grid grid-cols-3 gap-3 py-2.5"><dt className="text-slate-500">{l}</dt><dd className="col-span-2 whitespace-pre-wrap break-words text-slate-900">{show(item[k])}</dd></div>
              ))}
            </dl>
            {kind === "applicants" && (
              <div className="mt-6">
                <h3 className="text-sm font-bold text-[color:var(--navy)]">Dokumente</h3>
                {(item.files ?? []).length === 0 ? <p className="mt-2 text-sm text-slate-500">Keine Dateien hochgeladen.</p> : (
                  <ul className="mt-2 space-y-2">
                    {item.files.map((f) => (
                      <li key={f.file_id}>
                        <button onClick={() => download(f)} className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2.5 text-left text-sm hover:bg-slate-50" data-testid={`admin-file-download-${f.file_id}`}>
                          <span className="flex min-w-0 items-center gap-2"><FileText size={16} className="shrink-0" /><span className="truncate">{f.filename}</span></span>
                          {busy === f.file_id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="mt-6 grid gap-4">
              <label className="text-sm font-semibold text-[color:var(--navy)]" htmlFor="detail-status">Status</label>
              <select id="detail-status" className="field-input" value={item.status} onChange={(e) => patch({ status: e.target.value })} data-testid="admin-detail-status">
                {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <label className="text-sm font-semibold text-[color:var(--navy)]" htmlFor="detail-note">Interne Notiz</label>
              <textarea id="detail-note" rows={3} className="field-input" value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-detail-note" />
              <button onClick={() => patch({ internal_note: note })} className="justify-self-start rounded-full bg-[color:var(--navy)] px-5 py-2.5 text-sm font-semibold text-white" data-testid="admin-detail-save-note">Notiz speichern</button>
            </div>
            <div className="mt-6 rounded-xl bg-slate-50 p-4 text-xs text-slate-600 space-y-1">
              <p>Team-Benachrichtigung: {item.notification?.status ?? "–"}</p>
              <p>Odoo: {item.odoo?.status ?? "–"} {item.odoo?.error ? `(${item.odoo.error})` : ""}</p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 pb-6">
              <button disabled={!odooReady || busy === "odoo"} onClick={sync} title={odooReady ? "" : "Odoo ist noch nicht konfiguriert"} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-50" data-testid="admin-detail-odoo-sync">
                <RefreshCw size={15} className={busy === "odoo" ? "animate-spin" : ""} /> An Odoo übertragen
              </button>
              <button onClick={remove} className="inline-flex items-center gap-2 rounded-full border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50" data-testid="admin-detail-delete">
                <Trash2 size={15} /> Löschen
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
